import { z } from "zod";
import { SectionAccessSelectionSchema } from "./section-access.js";
const PasswordInputSchema = z
    .string()
    .min(1)
    .max(1_024)
    .refine((value) => new TextEncoder().encode(value).byteLength <= 1_024, {
    message: "Password must be at most 1024 UTF-8 bytes.",
});
const NewPasswordSchema = PasswordInputSchema.refine((value) => value.length >= 12, {
    message: "Password must contain at least 12 characters.",
});
export const TeamMemberRoleSchema = z.enum(["owner", "admin", "manager", "reviewer"]);
export const TeamManageableRoleSchema = z.enum(["admin", "manager", "reviewer"]);
export const TeamAccessCodeSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/u);
export const TeamMutationIdempotencyKeySchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/u);
export const TeamMfaAuthorizationCodeSchema = z.string().trim().refine((value) => /^\d{6}$/u.test(value) || /^[a-f0-9]{5}(?:-[a-f0-9]{5}){3}$/u.test(value), { message: "Use a six-digit authenticator code or a recovery code." });
export const TeamPrivilegedConfirmationSchema = z.object({
    currentPassword: PasswordInputSchema,
    mfaCode: TeamMfaAuthorizationCodeSchema,
    reason: z.string().trim().min(10).max(1_000),
    idempotencyKey: TeamMutationIdempotencyKeySchema,
});
export const TeamSessionConfirmationSchema = TeamPrivilegedConfirmationSchema.omit({ currentPassword: true, mfaCode: true });
export const TeamInvitationCreateSchema = TeamSessionConfirmationSchema.extend({
    sectionAccess: SectionAccessSelectionSchema.optional(),
    calendarAccess: z.object({ groupId: z.string().uuid(), managerId: z.string().uuid().nullable() }).strict().optional(),
    email: z.string().trim().toLowerCase().email().max(320),
    displayName: z.string().trim().min(2).max(200),
    role: TeamManageableRoleSchema,
});
export const TeamInvitationRevokeSchema = TeamSessionConfirmationSchema;
export const TeamRoleChangeSchema = TeamSessionConfirmationSchema.extend({
    role: TeamManageableRoleSchema,
});
export const TeamMemberDeactivateSchema = TeamSessionConfirmationSchema;
export const TeamPasswordResetCreateSchema = TeamPrivilegedConfirmationSchema;
export const TeamRestoreReprovisionCreateSchema = TeamPrivilegedConfirmationSchema;
export const TeamInvitationAcceptSchema = z.object({
    code: TeamAccessCodeSchema,
    newPassword: NewPasswordSchema,
});
export const TeamPasswordResetAcceptSchema = TeamInvitationAcceptSchema;
export const TeamMemberSchema = z.object({
    membershipId: z.string().uuid(),
    userId: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string().min(1).max(200),
    role: TeamMemberRoleSchema,
    state: z.enum([
        "active",
        "password_reset_required",
        "inactive",
        "restore_reprovision_required",
    ]),
    mfaEnrolled: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const TeamInvitationSchema = z.object({
    invitationId: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string().min(1).max(200),
    role: TeamManageableRoleSchema,
    purpose: z.enum(["new_member", "restore_reprovision"]),
    expiresAt: z.string().datetime({ offset: true }),
    createdAt: z.string().datetime({ offset: true }),
    createdByName: z.string().min(1).max(200),
});
export const TeamAccessSnapshotSchema = z.object({
    members: z.array(TeamMemberSchema).max(500),
    invitations: z.array(TeamInvitationSchema).max(500),
});
export const TeamOneTimeGrantSchema = z.object({
    emailDelivery: z.enum(["queued", "not_configured"]).optional(),
    grantId: z.string().uuid(),
    // Plaintext is returned only for the first committed response. A replay can
    // prove the prior grant but cannot reconstruct its hash-only code.
    code: TeamAccessCodeSchema.nullable(),
    expiresAt: z.string().datetime({ offset: true }),
    replayed: z.boolean(),
}).superRefine((value, context) => {
    if (value.replayed !== (value.code === null)) {
        context.addIssue({
            code: "custom",
            path: ["code"],
            message: "A replayed one-time grant must not expose a plaintext code.",
        });
    }
});
export const TeamMutationResultSchema = z.object({
    entityId: z.string().uuid(),
    replayed: z.boolean(),
});
export const TeamAccessAcceptResultSchema = z.object({
    userId: z.string().uuid(),
}).strict();
export const PasswordRecoveryRequestSchema = z.object({ email: z.string().trim().toLowerCase().email().max(320) }).strict();
export const PasswordRecoveryRequestedSchema = z.object({ accepted: z.literal(true) }).strict();
//# sourceMappingURL=team-access.js.map